import { useState, useEffect } from 'react';
import { Search, X, Truck, Package, AlertTriangle } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { CHART_PALETTE } from '../../config/colors';
import authenticatedFetch from '../../utils/authenticatedFetch';
import type { SelectedItem, TimeRange } from '../../tabs/FleetIntelligence';

// Predefined selection-color palette — categorical (one color per selected
// item), hue-matched onto the shared chart palette (10 slots; adjacent hues
// share a slot).
const COLOR_PALETTE = [
  CHART_PALETTE[0], // blue
  CHART_PALETTE[3], // red
  CHART_PALETTE[2], // green
  CHART_PALETTE[1], // amber
  CHART_PALETTE[4], // violet
  CHART_PALETTE[6], // pink
  CHART_PALETTE[9], // cyan
  CHART_PALETTE[8], // lime
  CHART_PALETTE[1], // orange
  CHART_PALETTE[4], // indigo
  CHART_PALETTE[9], // teal
  CHART_PALETTE[1], // yellow
];

interface VehicleListItem {
  entityId: string;
  identifiers: Array<{ type: string; value: string }>;
  source: 'optifleet' | 'declared' | 'unknown';
  lastSeen?: string;
  totalPoints?: number;
  isActive?: boolean;
}

interface OrderListItem {
  entityId: string;
  externalRef?: string;
  status?: string;
  customerReference?: string;
  source: 'qargo' | 'declared' | 'palletways' | 'unknown';
  stopCount: number;
  associatedVehicles?: string[];
  createdAt?: string;
}

interface OrderVehicleLink {
  vehicleEntityId: string;
  linkType: 'DIRECT_ID' | 'IDENTIFIER_MATCH' | 'DRIVER_MATCH' | 'SPATIOTEMPORAL_MATCH';
  confidence: number;
  status: string;
}

interface SelectionPanelProps {
  selectedItems: SelectedItem[];
  onSelectionsChange: (items: SelectedItem[]) => void;
  timeRange: TimeRange;
}

export default function SelectionPanel({
  selectedItems,
  onSelectionsChange,
  timeRange,
}: SelectionPanelProps) {
  const [vehicles, setVehicles] = useState<VehicleListItem[]>([]);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [orderLinks, setOrderLinks] = useState<Map<string, OrderVehicleLink[]>>(new Map());
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicleColors, setVehicleColors] = useState<Map<string, string>>(new Map());
  const [hoveredVehicleId, setHoveredVehicleId] = useState<string | null>(null);
  const [hoveredOrderId, setHoveredOrderId] = useState<string | null>(null);
  const [linksLoaded, setLinksLoaded] = useState(false);

  // Fetch vehicles and orders (fast - no link resolution)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setLinksLoaded(false);
      
      try {
        const vehiclesUrl = API_ENDPOINTS.fleetIntelligence.vehicles.list(timeRange);
        const ordersUrl = API_ENDPOINTS.fleetIntelligence.orders.list(timeRange);
        
        console.log('[Fleet Intelligence] Fetching from:', { vehiclesUrl, ordersUrl });
        
        const [vehiclesRes, ordersRes] = await Promise.all([
          authenticatedFetch(vehiclesUrl),
          authenticatedFetch(ordersUrl),
        ]);

        console.log('[Fleet Intelligence] Response statuses:', { 
          vehicles: vehiclesRes.status, 
          orders: ordersRes.status,
          vehiclesOk: vehiclesRes.ok,
          ordersOk: ordersRes.ok
        });

        if (!vehiclesRes.ok || !ordersRes.ok) {
          let vehiclesError = '';
          let ordersError = '';
          
          if (!vehiclesRes.ok) {
            vehiclesError = await vehiclesRes.text();
            console.error('[Fleet Intelligence] Vehicles error response:', vehiclesError);
          }
          if (!ordersRes.ok) {
            ordersError = await ordersRes.text();
            console.error('[Fleet Intelligence] Orders error response:', ordersError);
          }
          
          throw new Error(`Failed to fetch data: vehicles ${vehiclesRes.status}${vehiclesError ? ` - ${vehiclesError}` : ''}, orders ${ordersRes.status}${ordersError ? ` - ${ordersError}` : ''}`);
        }

        const vehiclesData = await vehiclesRes.json();
        const ordersData = await ordersRes.json();

        const vehiclesList = Array.isArray(vehiclesData?.vehicles) ? vehiclesData.vehicles : 
                             Array.isArray(vehiclesData) ? vehiclesData : [];
        const ordersList = Array.isArray(ordersData?.orders) ? ordersData.orders : 
                          Array.isArray(ordersData) ? ordersData : [];

        console.log('[Fleet Intelligence] Data received:', {
          vehicleCount: vehiclesList.length,
          orderCount: ordersList.length
        });

        const colorMap = new Map<string, string>();
        vehiclesList.forEach((vehicle: VehicleListItem, index: number) => {
          colorMap.set(vehicle.entityId, COLOR_PALETTE[index % COLOR_PALETTE.length]);
        });
        setVehicleColors(colorMap);
        setVehicles(vehiclesList);
        setOrders(ordersList);
      } catch (err: any) {
        setError(err.message);
        console.error('[Fleet Intelligence] Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  // Lazy load order-vehicle links AFTER main data is displayed
  useEffect(() => {
    if (orders.length === 0 || linksLoaded) return;

    const fetchLinks = async () => {
      const linksMap = new Map<string, OrderVehicleLink[]>();
      try {
        const batchUrl = API_ENDPOINTS.decisions.orderVehicleLinksBatch();
        const batchRes = await fetch(batchUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_ids: orders.map((o: OrderListItem) => o.entityId),
          }),
        });
        if (batchRes.ok) {
          const batchData = await batchRes.json();
          const results = batchData.results || {};
          for (const [orderId, decisions] of Object.entries(results)) {
            linksMap.set(orderId, (decisions as any[]) || []);
          }
        } else {
          // Fallback: individual calls (limited to first 20)
          const linkPromises = orders.slice(0, 20).map(async (order: OrderListItem) => {
            try {
              const linkUrl = API_ENDPOINTS.decisions.orderVehicleLinks(order.entityId);
              const linkRes = await fetch(linkUrl);
              if (linkRes.ok) {
                const linkData = await linkRes.json();
                return { orderId: order.entityId, links: linkData.decisions || [] };
              }
            } catch {
              // silently ignore individual failures in fallback
            }
            return { orderId: order.entityId, links: [] };
          });
          const linkResults = await Promise.all(linkPromises);
          linkResults.forEach(result => linksMap.set(result.orderId, result.links));
        }
      } catch (err) {
        console.warn('[Fleet Intelligence] Failed to fetch links:', err);
      }
      setOrderLinks(linksMap);
      setLinksLoaded(true);

      console.log('[Fleet Intelligence] Order links lazy-loaded:', {
        totalOrders: orders.length,
        ordersWithLinks: Array.from(linksMap.values()).filter(l => l.length > 0).length,
      });
    };

    // Defer link loading to next tick so UI renders first
    const timer = setTimeout(fetchLinks, 100);
    return () => clearTimeout(timer);
  }, [orders, linksLoaded]);

  const getVehicleColor = (vehicleId: string): string => {
    return vehicleColors.get(vehicleId) || COLOR_PALETTE[0];
  };

  const getVehicleLabel = (vehicle: VehicleListItem): string => {
    // Try to get registration first
    const reg = vehicle.identifiers.find(id => id.type === 'registration');
    if (reg?.value) return reg.value;
    
    // Fall back to VIN if available
    const vin = vehicle.identifiers.find(id => id.type === 'vin');
    if (vin?.value) return vin.value;
    
    // Last resort: use entity ID (shortened)
    return vehicle.entityId.slice(0, 8);
  };

  const getOrderColor = (order: OrderListItem): string => {
    // Use linked vehicle's map color (same palette as vehicles panel)
    if (order.associatedVehicles && order.associatedVehicles.length > 0) {
      return getVehicleColor(order.associatedVehicles[0]);
    }

    const links = orderLinks.get(order.entityId);
    if (links && links.length > 0) {
      const bestLink = [...links].sort((a, b) => b.confidence - a.confidence)[0];
      return getVehicleColor(bestLink.vehicleEntityId);
    }

    return COLOR_PALETTE[0];
  };
  
  const getOrderBestLink = (order: OrderListItem): OrderVehicleLink | null => {
    const links = orderLinks.get(order.entityId);
    if (!links || links.length === 0) return null;
    // Return highest confidence link
    return [...links].sort((a, b) => b.confidence - a.confidence)[0];
  };
  
  const getLinkTypeBadge = (linkType: string): string => {
    switch (linkType) {
      case 'DIRECT_ID':
        return '🎯';
      case 'IDENTIFIER_MATCH':
        return '🔗';
      case 'DRIVER_MATCH':
        return '👤';
      case 'SPATIOTEMPORAL_MATCH':
        return '📍';
      default:
        return '❓';
    }
  };
  
  const getLinkTypeLabel = (linkType: string): string => {
    switch (linkType) {
      case 'DIRECT_ID':
        return 'Direct';
      case 'IDENTIFIER_MATCH':
        return 'ID Match';
      case 'DRIVER_MATCH':
        return 'Driver';
      case 'SPATIOTEMPORAL_MATCH':
        return 'Location';
      default:
        return 'Unknown';
    }
  };

  const getOrderLabel = (order: OrderListItem): string => {
    if (order.externalRef) return order.externalRef;
    if (order.customerReference) return order.customerReference;
    return order.entityId.slice(0, 8);
  };

  const getOrderVehicleReg = (order: OrderListItem): string | null => {
    // First try explicit associations
    if (order.associatedVehicles && order.associatedVehicles.length > 0) {
      const vehicleId = order.associatedVehicles[0];
      const vehicle = vehicles.find(v => v.entityId === vehicleId);
      
      if (vehicle) {
        const reg = vehicle.identifiers.find(id => id.type === 'registration');
        if (reg?.value) return reg.value;
        const vin = vehicle.identifiers.find(id => id.type === 'vin');
        if (vin?.value) return vin.value;
      }
    }
    
    // Then try fuzzy match links
    const links = orderLinks.get(order.entityId);
    if (links && links.length > 0) {
      // Sort by confidence and take the best match
      const bestLink = [...links].sort((a, b) => b.confidence - a.confidence)[0];
      const vehicle = vehicles.find(v => v.entityId === bestLink.vehicleEntityId);
      
      if (vehicle) {
        const reg = vehicle.identifiers.find(id => id.type === 'registration');
        if (reg?.value) return reg.value;
        const vin = vehicle.identifiers.find(id => id.type === 'vin');
        if (vin?.value) return vin.value;
      }
    }
    
    return null;
  };

  const handleVehicleToggle = (vehicle: VehicleListItem) => {
    const existingIndex = selectedItems.findIndex(
      item => item.id === vehicle.entityId && item.type === 'vehicle'
    );

    if (existingIndex >= 0) {
      // Deselect
      const newItems = selectedItems.filter((_, i) => i !== existingIndex);
      onSelectionsChange(newItems);
    } else {
      // Check vehicle limit
      const vehicleCount = selectedItems.filter(item => item.type === 'vehicle').length;
      if (vehicleCount >= 10) {
        alert('Maximum 10 vehicles can be selected');
        return;
      }

      // Select
      const newItem: SelectedItem = {
        id: vehicle.entityId,
        type: 'vehicle',
        label: getVehicleLabel(vehicle),
        color: getVehicleColor(vehicle.entityId),
        source: vehicle.source,
      };
      onSelectionsChange([...selectedItems, newItem]);
    }
  };

  const handleOrderToggle = (order: OrderListItem) => {
    const existingIndex = selectedItems.findIndex(
      item => item.id === order.entityId && item.type === 'order'
    );

    if (existingIndex >= 0) {
      // Deselect
      const newItems = selectedItems.filter((_, i) => i !== existingIndex);
      onSelectionsChange(newItems);
    } else {
      // Check order limit
      const orderCount = selectedItems.filter(item => item.type === 'order').length;
      if (orderCount >= 10) {
        alert('Maximum 10 orders can be selected');
        return;
      }

      // Select
      const newItem: SelectedItem = {
        id: order.entityId,
        type: 'order',
        label: getOrderLabel(order),
        color: getOrderColor(order),
        source: order.source,
      };
      onSelectionsChange([...selectedItems, newItem]);
    }
  };

  const handleRemove = (item: SelectedItem) => {
    const newItems = selectedItems.filter(
      i => !(i.id === item.id && i.type === item.type)
    );
    onSelectionsChange(newItems);
  };

  const handleClearAll = () => {
    onSelectionsChange([]);
  };

  const isVehicleSelected = (vehicleId: string): boolean => {
    return selectedItems.some(item => item.id === vehicleId && item.type === 'vehicle');
  };

  const isOrderSelected = (orderId: string): boolean => {
    return selectedItems.some(item => item.id === orderId && item.type === 'order');
  };

  // Get associated vehicles for an order
  const getAssociatedVehicles = (orderId: string): string[] => {
    const order = orders.find(o => o.entityId === orderId);
    if (!order) return [];
    
    const vehicleIds: string[] = [];
    
    // Add explicit associations
    if (order.associatedVehicles) {
      vehicleIds.push(...order.associatedVehicles);
    }
    
    // Add vehicles from links
    const links = orderLinks.get(orderId);
    if (links) {
      links.forEach(link => {
        if (!vehicleIds.includes(link.vehicleEntityId)) {
          vehicleIds.push(link.vehicleEntityId);
        }
      });
    }
    
    return vehicleIds;
  };

  // Get associated orders for a vehicle
  const getAssociatedOrders = (vehicleId: string): string[] => {
    const orderIds: string[] = [];
    
    orders.forEach(order => {
      // Check explicit associations
      if (order.associatedVehicles?.includes(vehicleId)) {
        orderIds.push(order.entityId);
        return;
      }
      
      // Check links
      const links = orderLinks.get(order.entityId);
      if (links?.some(link => link.vehicleEntityId === vehicleId)) {
        orderIds.push(order.entityId);
      }
    });
    
    return orderIds;
  };

  // Check if vehicle should be highlighted and get color
  const getVehicleHighlightColor = (vehicleId: string): string | null => {
    if (hoveredVehicleId === vehicleId) {
      return getVehicleColor(vehicleId);
    }
    if (hoveredOrderId) {
      const associatedVehicles = getAssociatedVehicles(hoveredOrderId);
      if (associatedVehicles.includes(vehicleId)) {
        // Use the color of the first associated vehicle (the one being hovered via order)
        return getVehicleColor(associatedVehicles[0]);
      }
    }
    return null;
  };

  // Check if order should be highlighted and get color
  const getOrderHighlightColor = (orderId: string): string | null => {
    if (hoveredOrderId === orderId) {
      // Use the color of the first associated vehicle
      const associatedVehicles = getAssociatedVehicles(orderId);
      if (associatedVehicles.length > 0) {
        return getVehicleColor(associatedVehicles[0]);
      }
      return null;
    }
    if (hoveredVehicleId) {
      const associatedOrders = getAssociatedOrders(hoveredVehicleId);
      if (associatedOrders.includes(orderId)) {
        return getVehicleColor(hoveredVehicleId);
      }
    }
    return null;
  };

  // Filter vehicles and orders by search
  const filteredVehicles = vehicles.filter(vehicle => {
    if (!vehicleSearch) return true;
    const label = getVehicleLabel(vehicle).toLowerCase();
    return label.includes(vehicleSearch.toLowerCase());
  });

  const activeVehicles = filteredVehicles.filter(v => v.isActive);
  const inactiveVehicles = filteredVehicles.filter(v => !v.isActive);

  const filteredOrders = orders.filter(order => {
    if (!orderSearch) return true;
    const label = getOrderLabel(order).toLowerCase();
    return label.includes(orderSearch.toLowerCase());
  });

  // Data-source identity — categorical (chart palette, hue-matched).
  const getSourceBadgeColor = (source: string): string => {
    switch (source) {
      case 'optifleet':
        return 'bg-chart-1/20 text-chart-1 border-chart-1/30';
      case 'qargo':
        return 'bg-chart-5/20 text-chart-5 border-chart-5/30';
      case 'declared':
        return 'bg-chart-3/20 text-chart-3 border-chart-3/30';
      default:
        return 'bg-chart-8/20 text-chart-8 border-chart-8/30';
    }
  };

  return (
    <div className="space-y-4">
      {/* Selected Items Legend */}
      {selectedItems.length > 0 && (
        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">
              Selected ({selectedItems.length})
            </h3>
            <button
              onClick={handleClearAll}
              className="text-xs text-muted-foreground hover:text-destructive transition-colors"
            >
              Clear All
            </button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {selectedItems.map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                className="flex items-center justify-between gap-2 p-2 bg-secondary/50 rounded border border-border hover:border-accent transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0 border-2 border-white"
                    style={{ backgroundColor: item.color }}
                  />
                  {item.type === 'vehicle' ? (
                    <Truck className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <span className="text-sm text-white truncate">{item.label}</span>
                </div>
                <button
                  onClick={() => handleRemove(item)}
                  className="p-1 hover:bg-accent rounded transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-destructive/20 border border-destructive/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="text-sm text-destructive">
            <p className="font-medium">Error loading data</p>
            <p className="text-destructive/80">{error}</p>
          </div>
        </div>
      )}

      {/* Vehicles Section */}
      <div className="bg-card rounded-lg p-4 border border-border">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Truck className="w-4 h-4" />
          Vehicles (Active: {activeVehicles.length} / Inactive: {inactiveVehicles.length})
        </h3>
        
        {/* Search */}
        <div className="mb-3 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search vehicles..."
            value={vehicleSearch}
            onChange={(e) => setVehicleSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 bg-secondary border border-border rounded text-sm text-white placeholder-muted-foreground focus:outline-none focus:border-ring"
          />
        </div>

        {/* Vehicle List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
          ) : filteredVehicles.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No vehicles found</p>
          ) : (
            <>
              {/* Active Vehicles */}
              {activeVehicles.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-success mb-2 uppercase tracking-wide">
                    Active ({activeVehicles.length})
                  </h4>
                  <div className="space-y-2">
                    {activeVehicles.map((vehicle) => {
                      const selected = isVehicleSelected(vehicle.entityId);
                      const highlightColor = getVehicleHighlightColor(vehicle.entityId);
                      const vehicleColor = getVehicleColor(vehicle.entityId);
                      return (
                        <button
                          key={vehicle.entityId}
                          onClick={() => handleVehicleToggle(vehicle)}
                          onMouseEnter={() => setHoveredVehicleId(vehicle.entityId)}
                          onMouseLeave={() => setHoveredVehicleId(null)}
                          className={`w-full text-left p-2 rounded border transition-all ${
                            selected
                              ? 'bg-success/20 border-success hover:bg-success/30'
                              : highlightColor
                              ? 'ring-2'
                              : 'bg-secondary/50 border-border hover:border-accent'
                          }`}
                          style={highlightColor ? {
                            backgroundColor: `${highlightColor}20`,
                            borderColor: highlightColor,
                            boxShadow: `0 0 0 2px ${highlightColor}50`
                          } : undefined}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0 border border-white/30"
                                style={{ backgroundColor: vehicleColor }}
                              />
                              <span className="text-sm text-white truncate">
                                {getVehicleLabel(vehicle)}
                              </span>
                            </div>
                            <span
                              className={`text-xs px-2 py-0.5 rounded border ${getSourceBadgeColor(
                                vehicle.source
                              )}`}
                            >
                              {vehicle.source}
                            </span>
                          </div>
                          {vehicle.lastSeen && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Last: {new Date(vehicle.lastSeen).toLocaleString()}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Inactive Vehicles */}
              {inactiveVehicles.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                    Inactive ({inactiveVehicles.length})
                  </h4>
                  <div className="space-y-2">
                    {inactiveVehicles.map((vehicle) => {
                      const selected = isVehicleSelected(vehicle.entityId);
                      const highlightColor = getVehicleHighlightColor(vehicle.entityId);
                      const vehicleColor = getVehicleColor(vehicle.entityId);
                      return (
                        <button
                          key={vehicle.entityId}
                          onClick={() => handleVehicleToggle(vehicle)}
                          onMouseEnter={() => setHoveredVehicleId(vehicle.entityId)}
                          onMouseLeave={() => setHoveredVehicleId(null)}
                          className={`w-full text-left p-2 rounded border transition-all opacity-60 ${
                            selected
                              ? 'bg-success/20 border-success hover:bg-success/30'
                              : highlightColor
                              ? 'ring-2'
                              : 'bg-secondary/50 border-border hover:border-accent'
                          }`}
                          style={highlightColor ? {
                            backgroundColor: `${highlightColor}20`,
                            borderColor: highlightColor,
                            boxShadow: `0 0 0 2px ${highlightColor}50`
                          } : undefined}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0 border border-white/30"
                                style={{ backgroundColor: vehicleColor }}
                              />
                              <span className="text-sm text-white truncate">
                                {getVehicleLabel(vehicle)}
                              </span>
                            </div>
                            <span
                              className={`text-xs px-2 py-0.5 rounded border ${getSourceBadgeColor(
                                vehicle.source
                              )}`}
                            >
                              {vehicle.source}
                            </span>
                          </div>
                          {vehicle.lastSeen && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Last: {new Date(vehicle.lastSeen).toLocaleString()}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Orders Section */}
      <div className="bg-card rounded-lg p-4 border border-border">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Package className="w-4 h-4" />
          Orders ({filteredOrders.length})
        </h3>
        
        {/* Search */}
        <div className="mb-3 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search orders..."
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 bg-secondary border border-border rounded text-sm text-white placeholder-muted-foreground focus:outline-none focus:border-ring"
          />
        </div>

        {/* Order List */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
          ) : filteredOrders.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No orders found</p>
          ) : (
            filteredOrders.map((order) => {
              const selected = isOrderSelected(order.entityId);
              const highlightColor = getOrderHighlightColor(order.entityId);
              const orderColor = getOrderColor(order);
              const vehicleReg = getOrderVehicleReg(order);
              const bestLink = getOrderBestLink(order);
              
              return (
                <button
                  key={order.entityId}
                  onClick={() => handleOrderToggle(order)}
                  onMouseEnter={() => setHoveredOrderId(order.entityId)}
                  onMouseLeave={() => setHoveredOrderId(null)}
                  className={`w-full text-left p-2 rounded border transition-all ${
                    selected
                      ? 'bg-success/20 border-success hover:bg-success/30'
                      : highlightColor
                      ? 'ring-2'
                      : 'bg-secondary/50 border-border hover:border-accent'
                  }`}
                  style={highlightColor ? {
                    backgroundColor: `${highlightColor}20`,
                    borderColor: highlightColor,
                    boxShadow: `0 0 0 2px ${highlightColor}50`
                  } : undefined}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0 border border-white/30"
                        style={{ backgroundColor: orderColor }}
                      />
                      <span className="text-sm text-white truncate">
                        {getOrderLabel(order)}
                      </span>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded border ${getSourceBadgeColor(
                        order.source
                      )}`}
                    >
                      {order.source}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <p className="text-xs text-muted-foreground">
                      {order.stopCount} stops
                    </p>
                    {order.status && (
                      <p className="text-xs text-muted-foreground">
                        {order.status}
                      </p>
                    )}
                    {vehicleReg && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-success">
                          🚚 {vehicleReg}
                        </span>
                        {bestLink && (
                          <span 
                            className="text-xs px-1.5 py-0.5 rounded bg-ring/20 text-ring border border-ring/30"
                            title={`${getLinkTypeLabel(bestLink.linkType)} - ${Math.round(bestLink.confidence * 100)}% confidence`}
                          >
                            {getLinkTypeBadge(bestLink.linkType)} {Math.round(bestLink.confidence * 100)}%
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
